import { defineStore } from "pinia";

import type { CustomersState } from "@/Modules/Customers/Models/State";
import type { LegalAgreement } from "../Models/Domain/LegalAgreement";
import type { User } from "@/Modules/Models/Domain/User";
import type { DischargeReason, UserDischarge, DeleteAccount } from "./Models/Domain";
import { StateNamespace } from "@/Modules/App/Models/State/StateNamespace";

import fetchCustomerAH from "./ActionHandlers/FetchCustomer";
import SendMissingEmailActionHandler from "@/Modules/Therapist/ActionHandlers/SendMissingEmail";
import getLegalAgreementAH from "./ActionHandlers/GetLegalAgreement";
import acceptLegalAgreementsAH from "./ActionHandlers/AcceptLegalAgreement";
import dischargePatientAH from "./ActionHandlers/Discharge/DischargePatient";
import fetchDischargeReasonsAH from "./ActionHandlers/Discharge/FetchReasons";
import fetchUserDischargeAH from "./ActionHandlers/Discharge/FetchUserDischarge";
import requestTranscriptionAH from "./ActionHandlers/RequestTranscription";
import configTranscriptionAH from "./ActionHandlers/ConfigTranscription";
import deleteAccountAH from "./ActionHandlers/DeleteAccount";

import { hasValue } from "@/Utils/Objects";

const customerState: CustomersState = {
  customers: {},
  selected: null,
  isLoading: false,
  notification: null,
  isHasNoSubscription: false,
};

export const useCustomerStore = defineStore(StateNamespace.CUSTOMER, {
  state: () => ({ ...customerState }),
  getters: {
    getCurrentCustomer: (state: CustomersState) => {
      return state.selected;
    },
  },
  actions: {
    loadCustomer(customerId: string) {
      return fetchCustomerAH(customerId);
    },

    selectCustomer(customerId: string) {
      if (hasValue(this.customers[customerId])) {
        this.selected = this.customers[customerId];
      }
    },

    async fetchCustomer(customerId: string): Promise<void> {
      this.isLoading = true;

      if (!hasValue(this.customers[customerId])) {
        await this.loadCustomer(customerId);
      } else {
        this.selected = this.customers[customerId];
      }

      this.isLoading = false;
    },

    async addOrModifyCustomer(user: User) {
      await this.loadCustomer(user.id);
    },

    async sendMissingEmail(uuid: string) {
      const sendMissingEmailHandler = new SendMissingEmailActionHandler(this);

      await sendMissingEmailHandler.handle(uuid);
    },

    async getLegalAgreement(uuid: string, contract_uuid: string): Promise<LegalAgreement> {
      return getLegalAgreementAH(uuid, contract_uuid);
    },

    async acceptLegalAgreement(uuid: string, contractUUID: string): Promise<LegalAgreement | undefined> {
      return acceptLegalAgreementsAH(uuid, contractUUID);
    },

    dischargeCustomer(reason: string): Promise<true | undefined> {
      return dischargePatientAH(reason);
    },

    getDischargeReasons(): Promise<DischargeReason[] | undefined> {
      return fetchDischargeReasonsAH();
    },

    getUserDischarge(): Promise<UserDischarge | undefined> {
      return fetchUserDischargeAH();
    },

    requestTranscription(status: boolean): Promise<boolean | undefined> {
      return requestTranscriptionAH(status);
    },

    configTranscription(accept: boolean): Promise<boolean | undefined> {
      return configTranscriptionAH({ status: accept });
    },

    deleteAccount(customerId: string, deleteAccountData: DeleteAccount): Promise<boolean | undefined> {
      return deleteAccountAH(customerId, deleteAccountData);
    },
  },
});

export type CustomerStore = ReturnType<typeof useCustomerStore>;

export default useCustomerStore;
